import React, { useEffect, useState } from 'react';
import Modal from '../../modals/Modal';
import Spinner from '../../Spinner';
import SubscriptionStatusBadge from './SubscriptionStatusBadge';
import { fetchTeamSubscriptionDetails } from '../../../services/admin/subscriptionTrackerApi';
import { TeamSubscriptionDetailResponse } from '../../../types/admin/subscriptionTracker';
import { exportTeamPlanCsv } from '../../../utils/admin/exportTeamPlanCsv';
import {
  formatDaysRemaining,
  formatSubscriptionDate,
  getSportLabel,
} from '../../../utils/admin/formatSubscriptionDate';

interface ViewPlanModalProps {
  isOpen: boolean;
  teamId: number | null;
  teamName: string;
  onClose: () => void;
}

const ViewPlanModal: React.FC<ViewPlanModalProps> = ({
  isOpen,
  teamId,
  teamName,
  onClose,
}) => {
  const [data, setData] = useState<TeamSubscriptionDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || teamId == null) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);
      setData(null);

      const result = await fetchTeamSubscriptionDetails(teamId, controller.signal);

      if (result.aborted || controller.signal.aborted) return;

      if (result.status === 404) {
        setError('Team not found');
      } else if (result.data) {
        setData(result.data);
      } else {
        setError(result.error ?? 'Failed to load team plan');
      }

      setLoading(false);
    };

    load();

    return () => {
      controller.abort();
    };
  }, [isOpen, teamId]);

  const handleExport = () => {
    if (!data) return;
    exportTeamPlanCsv(data.team_name, data.team_code, data.sports);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdrop
      closeOnEsc
      panelClassName="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col relative mx-4"
      backdropClassName="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4"
    >
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {teamName} — Team Plan
        </h2>
        <div className="flex items-center gap-2 mr-8">
          <button
            type="button"
            onClick={handleExport}
            disabled={!data || loading}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading && (
          <div className="flex justify-center items-center h-40">
            <Spinner size="md" />
            <span className="ml-2 text-gray-600">Loading team plan...</span>
          </div>
        )}

        {error && !loading && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
        )}

        {data && !loading && !error && (
          <>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Sports for team:</h3>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sport</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">End</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Days Remaining</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Activated by</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.sports.map((sport) => (
                    <tr key={sport.sport_code}>
                      <td className="px-3 py-2 font-medium">{getSportLabel(sport.sport_code)}</td>
                      <td className="px-3 py-2">
                        <SubscriptionStatusBadge status={sport.display_status} />
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {sport.display_status === 'n/a' ? '—' : formatSubscriptionDate(sport.start_date)}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {sport.display_status === 'n/a' ? '—' : formatSubscriptionDate(sport.end_date)}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{formatDaysRemaining(sport.days_remaining)}</td>
                      <td className="px-3 py-2 text-gray-600">{sport.activated_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ViewPlanModal;
